import { Skeleton } from "app/components/Skeleton";
import {
  workItemIcons,
  workItemIconsColors,
} from "app/components/workItemIcons";
import { formatEffortShorthand } from "app/containers/Features/helper";
import { EffortEstimatedType } from "app/containers/Features/types";
import dayjs from "dayjs";
import { useQueryState } from "nuqs";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTicketUpdateActions } from "../../Ticket/components/useTicketUpdateActions";
import { toTitleCase } from "../helpers/Index";
import { selectProjectDetails } from "../redux/selector";
import { actions } from "../redux/slice";
import { IDashboardUserTicket } from "../type";
import EffortEstimated from "./EffortEstimated";
import HomeStatus from "./HomeStatus";
import { TimeTrackerTrigger } from "./TimeTrackerTrigger";

interface MyTicketsRowProps {
  row: IDashboardUserTicket;
  gridTemplateColumns: string;
}

const MyTicketsRow: React.FC<MyTicketsRowProps> = ({
  row,
  gridTemplateColumns,
}) => {
  const typeKey = row.type || "task";
  const IconComponent =
    workItemIcons[typeKey as keyof typeof workItemIcons] ||
    workItemIcons["task"];
  const iconColor =
    workItemIconsColors[typeKey as keyof typeof workItemIconsColors] || "#000";

  const [, setTid] = useQueryState("tid");
  const dispatch = useDispatch();

  const projectDetails = useSelector(selectProjectDetails);
  const project = projectDetails.find((p) => p._id === row.projectId);
  const projectName = project?.name || "";
  const { statusUpdate, effortEstimationUpdate } = useTicketUpdateActions({
    projectId: row.projectId || "",
  });

  const statusObj = project?.projectStatuses?.find((s) => s._id === row.status);
  const statusName = statusObj ? toTitleCase(statusObj.name) : "";
  const statusColor = statusObj?.color || "#9CA3AF";

  const availableStatuses = (project?.projectStatuses || []).map((s) => ({
    _id: s._id,
    name: toTitleCase(s.name),
    color: s.color,
  }));

  const dueDateFormatted = row.dueDate
    ? dayjs(row.dueDate).format("DD MMM, YYYY")
    : "-";

  const handleStatusUpdate = (newStatusId: string) => {
    if (!newStatusId || !row._id || !row.projectId) return;
    if (newStatusId === row.status) return;

    dispatch(
      actions.updateMyTicketFieldsLocally({
        ticketId: row._id,
        fields: { status: newStatusId },
      }),
    );
    statusUpdate(newStatusId, row._id);
  };

  const handleEffortUpdate = (effortEstimated: EffortEstimatedType) => {
    if (!row._id || !row.projectId) return;
    effortEstimationUpdate({ ...effortEstimated.effort }, row._id, () => {
      dispatch(
        actions.updateMyTicketFieldsLocally({
          ticketId: row._id,
          fields: { effortEstimated: [effortEstimated] },
        }),
      );
    });
  };

  return (
    <div
      className="grid h-9 items-center border-b border-[#E5E7EB] text-sm text-[#374151] transition-colors hover:bg-[#F9FAFB]"
      style={{ gridTemplateColumns }}
    >
      <div
        className="flex h-full items-center gap-3 truncate px-4 cursor-pointer"
        onClick={() => setTid(row._id)}
      >
        <div className="shrink-0">
          {IconComponent && (
            <IconComponent
              width={20}
              height={20}
              style={{ color: iconColor }}
            />
          )}
        </div>

        <div className="flex items-center gap-2 truncate">
          <span className="text-xs font-medium text-black">{row.ticketId}</span>
          <span className="truncate font-medium text-black" title={row.title}>
            {row.title}
          </span>
        </div>
      </div>

      <div className="flex h-full items-center truncate px-4 text-black">
        {!projectName || projectName === "" ? (
          <Skeleton className="h-4 w-24 rounded" />
        ) : (
          projectName
        )}
      </div>

      <div className="flex h-full w-full items-center px-4">
        {!statusName ? (
          <Skeleton className="h-6 w-24 rounded" />
        ) : (
          <HomeStatus
            status={row.status}
            color={statusColor}
            options={availableStatuses}
            onUpdate={handleStatusUpdate}
          />
        )}
      </div>

      <div className="flex h-full items-center whitespace-nowrap px-4 text-black">
        {dueDateFormatted}
      </div>
      <EffortEstimated row={row} onUpdate={handleEffortUpdate} />

      <div className="flex h-full items-center whitespace-nowrap px-4 font-medium text-black cursor-pointer">
        <TimeTrackerTrigger row={row}>
          <span>
            {formatEffortShorthand(
              row.totalEffortActual?.hours,
              row.totalEffortActual?.minutes,
            )}
          </span>
        </TimeTrackerTrigger>
      </div>
    </div>
  );
};

export default MyTicketsRow;
